/**
 * Condition profiles — maps a user's declared conditions to:
 *   autoSurface: which clinical sub-attributes to show automatically (not behind "More detail")
 *   suggestedSymptoms: symptom keys to surface as suggestions on the category picker
 *
 * Anomaly pairs: [normalValues, warnValue] — if a logged value is in warnValues and the
 * user's established pattern is a normalValue, surface a gentle informational nudge.
 */

export const CONDITIONS = [
  { id: 'endometriosis', label: 'Endometriosis' },
  { id: 'pcos', label: 'PCOS' },
  { id: 'pmdd', label: 'PMDD' },
  { id: 'adenomyosis', label: 'Adenomyosis' },
  { id: 'fibroids', label: 'Fibroids' },
  { id: 'perimenopause', label: 'Perimenopause / menopause' },
  { id: 'interstitial_cystitis', label: 'Interstitial cystitis' },
  { id: 'vulvodynia', label: 'Vulvodynia / vaginismus' },
];

export const CONDITION_PROFILES = {
  endometriosis: {
    autoSurface: {
      discharge: ['colour', 'odour', 'consistency'],
      bleeding: ['clots', 'colour', 'texture', 'odour'],
      cramps: ['type', 'location', 'timing'],
      pelvic_pressure: ['severity'],
      intimacy: ['comfort'],
      back_pain: ['location'],
    },
    suggestedSymptoms: ['cramps', 'pelvic_pressure', 'bleeding', 'fatigue', 'bloating', 'low_mood', 'back_pain'],
  },
  pcos: {
    autoSurface: {
      bleeding: ['flow', 'clots', 'colour'],
      discharge: ['amount', 'consistency'],
      skin_flare: ['type', 'area'],
    },
    suggestedSymptoms: ['bleeding', 'skin_flare', 'fatigue', 'low_mood', 'bloating', 'anxiety'],
  },
  pmdd: {
    autoSurface: {
      low_mood: ['feeling', 'intensity'],
      anxiety: ['severity'],
      brain_fog: ['severity'],
      cramps: ['severity', 'timing'],
    },
    suggestedSymptoms: ['low_mood', 'anxiety', 'brain_fog', 'fatigue', 'cramps', 'bloating'],
  },
  adenomyosis: {
    autoSurface: {
      bleeding: ['flow', 'clots', 'colour'],
      cramps: ['severity', 'location', 'type'],
      pelvic_pressure: ['severity'],
    },
    suggestedSymptoms: ['cramps', 'bleeding', 'pelvic_pressure', 'fatigue', 'bloating', 'back_pain'],
  },
  fibroids: {
    autoSurface: {
      bleeding: ['flow', 'clots'],
      pelvic_pressure: ['severity'],
      back_pain: ['severity', 'location'],
    },
    suggestedSymptoms: ['bleeding', 'pelvic_pressure', 'cramps', 'back_pain', 'fatigue'],
  },
  perimenopause: {
    autoSurface: {
      hot_flushes: ['timing', 'severity'],
      vaginal_dryness: ['severity'],
      sleep: ['quality', 'issues'],
      low_mood: ['feeling', 'intensity'],
      anxiety: ['severity'],
    },
    suggestedSymptoms: ['hot_flushes', 'vaginal_dryness', 'sleep', 'low_mood', 'fatigue', 'anxiety', 'brain_fog'],
  },
  interstitial_cystitis: {
    autoSurface: {
      pelvic_pressure: ['severity'],
      intimacy: ['comfort'],
    },
    suggestedSymptoms: ['pelvic_pressure', 'bloating', 'fatigue', 'low_mood'],
  },
  vulvodynia: {
    autoSurface: {
      intimacy: ['comfort'],
      vaginal_dryness: ['severity'],
    },
    suggestedSymptoms: ['vaginal_dryness', 'pelvic_pressure', 'anxiety', 'low_mood'],
  },
};

/**
 * Given a user's condition list, return a merged autoSurface map:
 * { symptomKey: Set<attributeKey> }
 * These attribute keys should be shown by default, not hidden behind "More detail".
 */
export function getAutoSurfaceMap(userConditions = []) {
  const map = {};
  userConditions.forEach(conditionId => {
    const profile = CONDITION_PROFILES[conditionId];
    if (!profile) return;
    Object.entries(profile.autoSurface).forEach(([symptomKey, attrKeys]) => {
      if (!map[symptomKey]) map[symptomKey] = new Set();
      attrKeys.forEach(k => map[symptomKey].add(k));
    });
  });
  return Object.fromEntries(Object.entries(map).map(([k, v]) => [k, [...v]]));
}

/**
 * Returns deduplicated list of suggested symptom keys for the user's conditions.
 */
export function getConditionSuggestions(userConditions = []) {
  const seen = new Set();
  const out = [];
  userConditions.forEach(conditionId => {
    const profile = CONDITION_PROFILES[conditionId];
    if (!profile) return;
    profile.suggestedSymptoms.forEach(key => {
      if (!seen.has(key)) { seen.add(key); out.push(key); }
    });
  });
  return out;
}

// ─── Anomaly detection ────────────────────────────────────────────────────────

/**
 * Symptom attribute combinations that warrant a gentle informational nudge
 * when they break from established pattern.
 * Format: { symptomKey, attributeKey, warnValues[], message }
 */
export const ANOMALY_RULES = [
  {
    symptomKey: 'discharge',
    attributeKey: 'colour',
    warnValues: ['Yellow', 'Green', 'Grey'],
    message: 'This colour can sometimes indicate infection. Worth noting if it persists.',
  },
  {
    symptomKey: 'discharge',
    attributeKey: 'consistency',
    warnValues: ['Chunky / cottage cheese'],
    message: 'This texture is sometimes associated with thrush. Worth checking with your GP if new.',
  },
  {
    symptomKey: 'discharge',
    attributeKey: 'odour',
    warnValues: ['Strong / unusual'],
    message: 'An unusual odour can sometimes indicate an imbalance worth discussing with your GP.',
  },
  {
    symptomKey: 'bleeding',
    attributeKey: 'odour',
    warnValues: ['Strong / unusual'],
    message: 'Unusual bleeding odour is worth mentioning at your next appointment.',
  },
  {
    symptomKey: 'intimacy',
    attributeKey: 'comfort',
    warnValues: ['Painful'],
    message: 'Painful sex (dyspareunia) is common but not something to ignore — your GP or gynaecologist can help.',
  },
];

/**
 * Check if a newly logged symptom + attribute triggers an anomaly nudge.
 * @param {string} symptomKey
 * @param {object} attributes
 * @param {object[]} recentSessions — last N sessions for pattern context
 * @returns {string|null} message or null
 */
export function checkAnomaly(symptomKey, attributes, recentSessions = []) {
  for (const rule of ANOMALY_RULES) {
    if (rule.symptomKey !== symptomKey) continue;
    const value = attributes[rule.attributeKey];
    if (!value || !rule.warnValues.includes(value)) continue;

    // Only nudge if the value is different from their recent pattern
    const recentValues = recentSessions
      .flatMap(s => s.symptoms || [])
      .filter(s => s.key === symptomKey)
      .map(s => s.attributes?.[rule.attributeKey])
      .filter(Boolean);

    // If they've logged this value before in recent history, don't repeat the nudge
    if (recentValues.includes(value)) continue;

    return rule.message;
  }
  return null;
}
